/**
 * EmploymentHistory — vertical timeline from `ai_fields.resume.employment_history`.
 * The current job is a filled navy marker; past jobs are hollow.
 */
import type { EmploymentEntry } from '@/features/pipeline/types';
import { dateRange, tenureLabel } from './resume-format';

export function EmploymentHistory({ entries }: { entries: EmploymentEntry[] }) {
  if (entries.length === 0) {
    return <p className="text-[13px] text-muted">No work history was found in the resume.</p>;
  }
  return (
    <ol className="relative ml-1.5 border-l border-gray-200 pl-4" aria-label="Employment history">
      {entries.map((e, i) => {
        const tenure = tenureLabel(e);
        const range = dateRange(e);
        return (
          <li key={`${e.employer}-${e.start ?? i}`} className="relative pb-4 last:pb-0">
            <span
              aria-hidden
              className={`absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full ${
                e.is_current ? 'bg-navy ring-2 ring-navy/20' : 'border-2 border-gray-400 bg-white'
              }`}
            />
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <span className="text-[13px] font-semibold leading-5 text-gray-900">{e.title || 'Role not stated'}</span>
              {e.is_current && (
                <span className="rounded-full bg-navy/10 px-1.5 py-0.5 text-[12px] font-medium text-navy">Current</span>
              )}
            </div>
            <div className="text-[13px] leading-5 text-gray-800">
              {e.employer || 'Employer not stated'}
              {e.location && <span className="text-muted"> · {e.location}</span>}
            </div>
            {(range || tenure) && (
              <div className="text-[12px] tabular-nums text-muted">
                {range}
                {range && tenure && <span aria-hidden> · </span>}
                {tenure}
              </div>
            )}
            {e.highlights.length > 0 && (
              <ul className="mt-1 list-disc space-y-0.5 pl-4 text-[13px] leading-5 text-gray-800 marker:text-gray-400">
                {e.highlights.map((h, j) => (
                  <li key={j}>{h}</li>
                ))}
              </ul>
            )}
          </li>
        );
      })}
    </ol>
  );
}
